import { z } from "zod";

/**
 * Which kind of thing a memory belongs to.
 *
 * 🔴 THIS IS THE ONLY DECLARATION OF THE SET, AND THE BACKEND'S
 * `agent-memory/domain/value-objects/agent-memory-subject.ts` RE-EXPORTS IT RATHER THAN
 * DECLARING ITS OWN. The set is pinned in the database by
 * `AgentMemory_scope_matches_arm`, which enumerates the three by name — so a second
 * copy on the surface a browser reaches would be a looser copy of a CHECK, which is a
 * bypass rather than a duplicate. One declaration, two readers, and `pnpm typecheck`
 * fails the day they disagree because there is nothing to disagree with.
 *
 * ⚠️ `MemorySchema.scope` IS A DIFFERENT COLUMN AND PINS ONLY FORM (`^[a-z][a-z0-9_]*$`).
 * The two share a word and are governed differently; do not use this enum to validate one.
 */
export const AGENT_MEMORY_SCOPES = ["chat", "customer", "agent"] as const;

export const AgentMemoryScopeSchema = z.enum(AGENT_MEMORY_SCOPES);

export type AgentMemoryScope = z.infer<typeof AgentMemoryScopeSchema>;

/**
 * The one scope the agent memory tool writes at — and therefore the only scope a builder
 * can usefully declare fields for.
 *
 * 🔴 ONE DECLARATION, TWO READERS. The backend re-exports this from
 * `agent-memory/domain/value-objects/memory-tool-schema.ts`, where the executor resolves
 * the subject and `AgentToolConfigsService` derives the tool's schema from the active
 * declaration at this scope. The frontend's memory settings declare fields at the SAME
 * constant. Two copies compared by a test would only catch a disagreement after it was
 * written; one copy cannot disagree with itself.
 */
export const AGENT_MEMORY_TOOL_SCOPE = "customer" as const satisfies AgentMemoryScope;

/**
 * The subject, as two path segments.
 *
 * The three arms are `@db.Uuid`, so a non-canonical id reaches Postgres as SQLSTATE
 * 22P02 — unmapped by `PrismaClientExceptionFilter`, which renders a malformed REQUEST
 * as a 500. Refusing it here makes it the 400 it is.
 */
export const AgentMemoryReadPathVarsSchema = z.object({
  scope: AgentMemoryScopeSchema,
  subjectId: z.string().uuid()
});

/**
 * One stored entry, as a browser reads it.
 *
 * 🔴 THIS IS WHAT IS STORED — IT IS NOT WHAT THE MODEL WAS SHOWN. The block below is
 * that, and the two can legitimately differ: the renderer strips line terminators,
 * trims, JSON-quotes, and DROPS whole entries that do not fit the budget. A surface
 * that renders these rows and calls them "what the agent sees" is wrong on exactly the
 * subjects where it matters.
 *
 * `updatedAt` is an ISO-8601 string. It is carried because auditing what an agent
 * remembers about a customer is the point of showing these rows at all, and "when was
 * this written" is half of that audit; the column is already selected by the read, so
 * it costs no query.
 */
export const AgentMemoryEntryViewSchema = z.object({
  key: z.string(),
  value: z.string(),
  updatedAt: z.string()
});

export type AgentMemoryEntryView = z.infer<typeof AgentMemoryEntryViewSchema>;

/**
 * What the model is shown, and how much of the record reached it.
 *
 * 🔴 `omittedEntries` IS A CAPACITY FACT, NOT AN INTEGRITY ONE, AND IT IS NEVER A PAGE.
 * It means *"the rendered block was too large to send in full"* — nothing more. There is
 * no cursor, no `hasMore` and no `limit` in this contract, because there is nothing to
 * fetch: `entries` is already the whole stored set.
 *
 * 🚨 IT DID CLAIM TO BE AN INTEGRITY ALARM AND THAT WAS WRONG — DO NOT RENDER IT AS ONE.
 * The claim was that the write door caps the counter at 8,000, so anything that does not
 * fit must be drift. That silently assumed the render budget and the stored charge were
 * the same unit. They are not — a rendered line always costs more than the charge it was
 * counted under — so a subject sitting legally at the cap dropped entries and raised the
 * alarm with its counter holding perfectly. The budget is now sized from the worst-case
 * rendering of a legal subject, so a healthy subject can no longer be trimmed; the
 * meaning of the field is still capacity.
 *
 * 🔑 THE DRIFT SIGNAL IS THE `integrity` FIELD, WHICH SERVES THE VERDICT RATHER THAN ITS
 * OPERANDS. Read `integrity.status`; do not compare numbers yourself.
 *
 * `bytes` is the UTF-8 size of `text` — a THIRD unit again, counting framing and quoting.
 * It is expected to differ from both of the above on every healthy subject.
 */
export const AgentMemoryBlockSchema = z.object({
  text: z.string(),
  bytes: z.number().int().nonnegative(),
  renderedEntries: z.number().int().nonnegative(),
  omittedEntries: z.number().int().nonnegative()
});

export type AgentMemoryBlock = z.infer<typeof AgentMemoryBlockSchema>;

/**
 * `AgentMemory.memoryBytes` as stored — the counter's own claim about what the entries
 * charge, where `block.bytes` is what the render measured.
 *
 * 🔴 THIS IS A CAPACITY NUMBER FOR DISPLAY — "how full is this subject" against the
 * 8,000-byte cap. **It is NOT the drift operand; `integrity` is the drift answer.** Do not
 * compare it to `block.bytes` either: that is the RENDERED size, a different unit again,
 * expected to differ on every healthy subject.
 */
const StoredMemoryBytesSchema = z.number().int().nonnegative();

/**
 * Whether the denormalised counter still agrees with the rows it summarises.
 *
 * 🔴 THE VERDICT IS SERVED, NOT ITS OPERANDS, AND THAT IS THE WHOLE POINT OF THE SHAPE.
 * This started as two numbers plus a sentence telling the client to compare them, and a
 * rule every reader has to re-implement is a rule some reader gets wrong — the first one
 * to get it wrong was this contract itself, which declared any inequality to be drift and
 * so reported every NEVER-WRITTEN subject as drifted, because an absent counter (`null`)
 * is not equal to a charge of `0`. That is the most common state there is.
 *
 * 🔑 SO THE NO-ROW CASE IS AN ARM OF THE TYPE, NOT A NULL A READER MUST REMEMBER TO TEST.
 * `not-applicable` carries no numbers at all: there is no counter, so there is nothing to
 * disagree with, and there is no `0` lying around to be compared against anything.
 *
 * ✅ MAGNITUDE SURVIVES, WHICH A BOOLEAN WOULD HAVE THROWN AWAY. `drifted` carries both
 * operands and the signed delta, so an operator can judge severity and direction —
 * positive means the rows charge MORE than the counter claims. `holding` carries the
 * charge because it is a useful number on its own and, there, it equals the counter.
 *
 * ⚠️ Computed server-side because the formula is a database CHECK
 * (`AgentMemoryEntry_charge_bounded`); a client re-deriving `key + value + 4` would be a
 * second copy of a constraint, free to drift from the one the write door enforces.
 */
export const AgentMemoryIntegritySchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("not-applicable") }),
  z.object({ status: z.literal("holding"), chargeBytes: z.number().int().nonnegative() }),
  z.object({
    status: z.literal("drifted"),
    counterBytes: z.number().int().nonnegative(),
    chargeBytes: z.number().int().nonnegative(),
    /** `chargeBytes - counterBytes`. Signed: positive means the rows charge more. */
    deltaBytes: z.number().int()
  })
]);

export type AgentMemoryIntegrity = z.infer<typeof AgentMemoryIntegritySchema>;

/**
 * One subject's memory: what is stored, and what the model was shown.
 *
 * 🔴 A DISCRIMINATED UNION SO THAT "COULD NOT READ" CANNOT BE CONSUMED AS "NOTHING
 * STORED". Those are opposite facts — one says *ask the customer*, the other says *do
 * not assume and do not confirm anything* — and the shape that collapses them is an
 * `entries: []` a caller reaches for without checking a sibling flag first. On the
 * `unavailable` arm the key is **not present at all**, so `data.entries.map(...)` is a
 * compile error until the state has been narrowed. A boolean beside an array would have
 * been advice; this is a wall.
 *
 * The cardinality bounds are the invariant, pinned rather than described:
 *
 * - `empty` ⟺ zero entries. The renderer returns it only when it produced no lines AND
 *   omitted nothing, which can only happen over an empty entry list.
 * - `present` ⟺ at least one entry. Every entry either renders a line or increments
 *   `omittedEntries`, so a `present` document had something to work from.
 * `storedMemoryBytes` is NULLABLE on BOTH readable arms, and `null` has exactly one
 * meaning: **no `AgentMemory` row exists for this subject in this organisation at all.**
 * (Which is also what another tenant's subject looks like from here — deliberately
 * indistinguishable, or the response would be a cross-tenant existence oracle.)
 *
 * 🔴 DO NOT RE-TIGHTEN IT TO NON-NULLABLE ON `present`. That is true in FACT — a
 * `present` document required at least one entry, which required a row — but it is not
 * provable to `tsc` from the producer, whose `storedMemoryBytes` is `number | null`. A
 * contract more precise than the use case that fills it does not remove the gap, it
 * MOVES it into the mapper, and a mapper has only two moves there: throw on a branch it
 * believes unreachable, or invent a value. Both are worse than carrying the nullability
 * honestly, and the second is how a fabricated `0` reaches a UI that then reports a
 * healthy counter for a subject that has none. Carry the `null`; document what it means.
 */
export const AgentMemoryReadResponseSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("unavailable"),
    block: AgentMemoryBlockSchema
  }),
  z.object({
    state: z.literal("empty"),
    entries: z.array(AgentMemoryEntryViewSchema).max(0),
    block: AgentMemoryBlockSchema,
    storedMemoryBytes: StoredMemoryBytesSchema.nullable(),
    integrity: AgentMemoryIntegritySchema
  }),
  z.object({
    state: z.literal("present"),
    entries: z.array(AgentMemoryEntryViewSchema).min(1),
    block: AgentMemoryBlockSchema,
    /** `null` when no `AgentMemory` row exists. See the union's docblock — do not re-tighten. */
    storedMemoryBytes: StoredMemoryBytesSchema.nullable(),
    integrity: AgentMemoryIntegritySchema
  })
]);

export type AgentMemoryRead = z.infer<typeof AgentMemoryReadResponseSchema>;
